import { useState, useEffect, useRef, Suspense } from 'react'
import { AxiosError } from 'axios'
import { useLoaderData, redirect, useNavigate, Link } from 'react-router'
import { parseAsArrayOf, parseAsString, useQueryState, createLoader } from 'nuqs'
import { createClient } from '@/api-client'
import { Subscriptions } from '@/api-client/Subscriptions'
import { Me } from '@/api-client/Me'
import { SubscriptionSchedules } from '~/api-client/SubscriptionSchedules'
import { Addresses } from '@/api-client/Addresses'
import { Cards } from '@/api-client/Cards'
import { SubscriptionSchedule, Card, SubscriptionInventory, FlowerCollectionProduct } from '@/api-client/data-contracts'
import { ScheduleSelector } from '~/components/subscription/branch-checkout/schedule-selector'
import { EstimatedDelivery } from '~/components/subscription/branch-checkout/estimated-delivery'
import { VaseSelector } from '~/components/subscription/branch-checkout/vase-selector'
import { CreditCardForm } from '~/components/subscription/branch-checkout/credit-card-form'
import { PriceTable } from '~/components/subscription/branch-checkout/price-table'
import logo from './logo.svg'

const loadSearchParams = createLoader({ inventory_ids: parseAsArrayOf(parseAsString).withDefault([]) })

export const loader = async ({ request }) => {
  const [account, schedules, addresses, subscriptions, inBulkPurchasables] = await Promise.all([
    fetchMe().catch(() => ({ data: { data: null } })),
    fetchSchedules(),
    fetchAddresses().catch(() => ({ data: { data: [] } })),
    fetchSubscriptions().catch(() => ({ data: { data: [] } })),
    fetchInBulkPurchasables(),
  ])

  const included = schedules.data.included || []
  const { inventory_ids: inventoryIds } = loadSearchParams(request)
  const inventories = included.filter((item) => item.type === 'subscription_inventory' && inventoryIds.includes(item.id))

  if (inventories.length === 0) {
    const inventory = included.find((item) => item.type === 'subscription_inventory' && !item.attributes.is_soldout)

    if (inventory) {
      return redirect(`/subscription/branch/new?inventory_ids=${inventory.id}`)
    } else {
      return redirect('/subscription/branch')
    }
  }

  return [account, schedules, addresses, inBulkPurchasables, subscriptions] as const
}

function fetchMe() {
  return createClient(Me).getMe()
}

function fetchSchedules(postalCode?: string) {
  return createClient(SubscriptionSchedules).getSubscriptionSchedules({ postal_code: postalCode, upcoming: '1', category: 'green' })
}

function fetchAddresses() {
  return createClient(Addresses).getAddresses()
}

function findSchedule(schedules: SubscriptionSchedule[], inventoryId: string) {
  return schedules.find((schedule) => schedule.relationships.inventory.data.id === inventoryId)
}

function fetchInBulkPurchasables() {
  return createClient(Subscriptions).getSubscriptionsInBulkPurchasables({ category: 'green' })
}

function fetchDefaultCard() {
  return createClient(Cards).getCardsDefault()
}

function fetchSubscriptions() {
  return createClient(Subscriptions).getSubscriptions({ category: 'green' })
}

const creditCardPromise = fetchDefaultCard()
  .then((res) => {
    const { data } = res.data

    if (data && !data.attributes.is_expired) {
      return data
    } else {
      return null
    }
  })
  .catch(() => null)

function createSubscriptionAddress(address) {
  const params = {
    kind: address.with_sender_name ? 'gift' : 'home',
    sender_name: address.with_sender_name ? address.sender_name : '',
    postal_code: address.postal_code,
    state: address.state,
    city_and_street: address.city_and_street,
    others: address.others,
    name: address.name,
    phone_number: address.phone_number,
  }

  return createClient(Subscriptions).postSubscriptionsAddresses(params)
}

export const SubscriptionBranchNew: React.FC = () => {
  const [
    {
      data: { data: account },
    },
    {
      data: { data: loaderSchedules, included },
    },
    {
      data: { data: addresses },
    },
    {
      data: { data: inBulkOrderables },
    },
    {
      data: { data: subscriptions },
    },
  ] = useLoaderData<typeof loader>()

  const navigate = useNavigate()
  const cityAndStreetInput = useRef<HTMLInputElement>(null)
  const [inventoryIds, setInventoryIds] = useQueryState('inventory_ids', parseAsArrayOf(parseAsString).withDefault([]))
  const inventories = included.filter((item) => item.type === 'subscription_inventory' && inventoryIds.includes(item.id)) as unknown as SubscriptionInventory[]
  const upcomingInventory = inventories.find((i) => !i.attributes.is_soldout)
  const [isAddressExpanded, setIsAddressExpanded] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [streetError, setStreetError] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [schedules, setSchedules] = useState(loaderSchedules)
  const [schedule, setSchedule] = useState(() => findSchedule(schedules, upcomingInventory?.id))
  const [delayedWeek, setDelayedWeek] = useState(0)
  const [creditCard, setCreditCard] = useState<Card | null>(null)
  const [holderName, setHolderName] = useState('')
  const [numberElement, setNumberElement] = useState(null)
  const [email, setEmail] = useState(account ? account.attributes.email : '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [inBulkOrderable, setInBulkOrderable] = useState<FlowerCollectionProduct | null>(null)
  const [order, setOrder] = useState(null)
  const [address, setAddress] = useState(() => {
    const primary = addresses.find((a) => a.attributes.is_primary)

    if (primary) {
      return {
        ...primary.attributes,
        with_sender_name: !!primary.attributes.sender_name,
      }
    } else {
      return {
        name: '',
        postal_code: '',
        state: '',
        city_and_street: '',
        others: '',
        phone_number: '',
        with_sender_name: false,
        sender_name: '',
      }
    }
  })

  const isAlreadySubscribed = subscriptions.length > 0
  const inputClass = 'w-full border border-gray rounded-lg px-3 py-3 text-[14px] outline-none focus:border-black transition-colors bg-white'

  useEffect(() => {
    setIsAddressExpanded(address.postal_code.trim().replace(/-/g, '').length >= 7)
  }, [address.postal_code])

  useEffect(() => {
    const postalCode = address.postal_code.replace('-', '')

    if (isAddressExpanded && postalCode.length === 7) {
      createClient(Subscriptions)
        .postSubscriptionsEstimate({
          inventory_ids: inventoryIds,
          in_bulk_product_ids: inBulkOrderable ? [inBulkOrderable.id] : [],
          postal_code: postalCode,
        })
        .then((res) => setOrder(res.data.data))
        .catch(() => setOrder(null))
    } else {
      setOrder(null)
    }
  }, [inventoryIds.join(), inBulkOrderable?.id, isAddressExpanded, address.postal_code])

  useEffect(() => {
    const postalCode = address.postal_code.replace('-', '')

    if (postalCode.length < 7) return
    if (!upcomingInventory) return

    fetchSchedules(postalCode).then((response) => {
      const { data: schedules, included } = response.data
      const schedule = findSchedule(schedules, upcomingInventory.id)
      setSchedules(schedules)
      setSchedule(schedule)

      if (schedule) {
        const delayId = schedule.relationships.delay.data?.id
        const delay = included.find((item) => item.type === 'subscription_schedule_delay' && item.id === delayId)
        const days = delay?.attributes?.days || 0
        setDelayedWeek(Math.ceil(days / 7))
      }
    })

    if (!address.city_and_street) {
      window
        .fetch(`https://joe-noh.github.io/jp-postal-code-api/api/v1/${postalCode}.json`)
        .then((res) => res.json())
        .then((json) => {
          const addr = json['addresses'][0]['ja']

          if (addr) {
            setAddress((prev) => ({
              ...prev,
              state: addr['prefecture'],
              city_and_street: addr['address1'] + addr['address2'],
            }))
          }
        })
        .catch(() => { }) // 補完に失敗しても無視
    }
  }, [address.postal_code, upcomingInventory?.id])

  const handleChangeAddress = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    setAddress((prev) => ({ ...prev, [name]: value }))
  }

  const handleUpdateCard = ({ holderName, numberElement }) => {
    setHolderName(holderName)
    setNumberElement(numberElement)
  }

  const validateCityAndStreet = () => {
    const input = cityAndStreetInput.current

    if (input && !input.value.match(/[０-９\d]/)) {
      const message = '番地が記載されていません。ご確認ください'

      input.setCustomValidity(message)
      setStreetError(message)
    } else {
      input?.setCustomValidity('')
      setStreetError('')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    validateCityAndStreet()

    if (!isSubmitting) {
      setIsSubmitting(true)

      try {
        if (!account) {
          await createClient(Subscriptions).postSubscriptionsSessions({ email, password, signup: '1' })
        }

        const formData = new FormData()
        inventories.forEach((inventory) => formData.append('inventory_ids[]', inventory.id))
        formData.append('payment_method', 'credit_card')

        if (inBulkOrderable) {
          formData.append('in_bulk_orderable_id', inBulkOrderable.id)
        }

        const newAddress = await createSubscriptionAddress(address)
        formData.append('address_id', newAddress.data.data.id)

        if (creditCard) {
          const response = await window.fetch('/subscription/checkout', {
            method: 'POST',
            body: formData,
            credentials: 'include',
            headers: {
              'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content,
            },
          })

          if (response.ok) {
            navigate('/subscription/branch/subscribed')
          } else {
            const { error } = await response.json()

            setErrorMessage(error.message_displayable)
          }
        } else {
          window.payjpInstance ||= window.Payjp(window.payjpPublicKey, { threeDSecureWorkflow: 'redirect' });

          const res = await window.payjpInstance.createToken(numberElement, {
            three_d_secure: true,
            card: { email, name: holderName },
          })

          if ('id' in res) {
            formData.append('card_token', res.id)

            const response = await window.fetch('/subscription/checkout', {
              method: 'POST',
              body: formData,
              credentials: 'include',
              headers: {
                'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content,
              },
            })

            if (response.ok) {
              const { tds_url } = await response.json()

              location.href = tds_url
            } else {
              const { error } = await response.json()

              setErrorMessage(error.message_displayable)
            }
          }
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          setErrorMessage(error.response?.data.error.message_displayable || '申し込みに失敗しました。入力内容をご確認ください')
        } else {
          setErrorMessage((error as Error).message)
        }
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  return (
    <article className="min-h-screen bg-bg font-sans text-main">
      <header className="bg-white border-b border-gray/40">
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/subscription/branch">
            <img src={logo} alt="FLOWER" className="h-7" />
          </Link>
        </div>
      </header>

      <form className="max-w-2xl mx-auto px-5 py-12 pb-32" onSubmit={handleSubmit}>
        <h1 className="text-[22px] font-medium text-black leading-relaxed mb-8">お申し込み手続き</h1>
        <section className="bg-white rounded-2xl p-6 mb-6">
          <h2 className="text-[15px] font-medium text-black mb-6">お届け先</h2>

          <div className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-[12px] text-dark-gray mb-2">
                お名前 <span className="text-accent">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="山田 花子"
                required
                value={address.name}
                className={inputClass}
                onChange={handleChangeAddress}
              />
            </div>

            <div>
              <label htmlFor="postal-code" className="block text-[12px] text-dark-gray mb-2">
                郵便番号 <span className="text-accent">*</span>
              </label>
              <div className="flex gap-3 items-center">
                <input
                  type="text"
                  id="postal-code"
                  name="postal_code"
                  placeholder="0000000"
                  required
                  value={address.postal_code}
                  onChange={handleChangeAddress}
                  className="w-40 border border-gray rounded-lg px-3 py-3 text-[14px] outline-none focus:border-black transition-colors bg-white"
                />
                {!isAddressExpanded && <span className="text-[12px] text-dark-gray">入力すると住所が展開されます</span>}
              </div>
            </div>

            {isAddressExpanded && (
              <>
                <div className="space-y-5 pt-4 border-t border-gray/40">
                  <div>
                    <label htmlFor="state" className="block text-[12px] text-dark-gray mb-2">
                      都道府県 <span className="text-accent">*</span>
                    </label>
                    <select id="state" name="state" required value={address.state} className={inputClass} onChange={handleChangeAddress}>
                      <option value="">選択してください</option>
                      <option value="北海道">北海道</option>
                      <option value="青森県">青森県</option>
                      <option value="岩手県">岩手県</option>
                      <option value="宮城県">宮城県</option>
                      <option value="秋田県">秋田県</option>
                      <option value="山形県">山形県</option>
                      <option value="福島県">福島県</option>
                      <option value="茨城県">茨城県</option>
                      <option value="栃木県">栃木県</option>
                      <option value="群馬県">群馬県</option>
                      <option value="埼玉県">埼玉県</option>
                      <option value="千葉県">千葉県</option>
                      <option value="東京都">東京都</option>
                      <option value="神奈川県">神奈川県</option>
                      <option value="新潟県">新潟県</option>
                      <option value="富山県">富山県</option>
                      <option value="石川県">石川県</option>
                      <option value="福井県">福井県</option>
                      <option value="山梨県">山梨県</option>
                      <option value="長野県">長野県</option>
                      <option value="岐阜県">岐阜県</option>
                      <option value="静岡県">静岡県</option>
                      <option value="愛知県">愛知県</option>
                      <option value="三重県">三重県</option>
                      <option value="滋賀県">滋賀県</option>
                      <option value="京都府">京都府</option>
                      <option value="大阪府">大阪府</option>
                      <option value="兵庫県">兵庫県</option>
                      <option value="奈良県">奈良県</option>
                      <option value="和歌山県">和歌山県</option>
                      <option value="鳥取県">鳥取県</option>
                      <option value="島根県">島根県</option>
                      <option value="岡山県">岡山県</option>
                      <option value="広島県">広島県</option>
                      <option value="山口県">山口県</option>
                      <option value="徳島県">徳島県</option>
                      <option value="香川県">香川県</option>
                      <option value="愛媛県">愛媛県</option>
                      <option value="高知県">高知県</option>
                      <option value="福岡県">福岡県</option>
                      <option value="佐賀県">佐賀県</option>
                      <option value="長崎県">長崎県</option>
                      <option value="熊本県">熊本県</option>
                      <option value="大分県">大分県</option>
                      <option value="宮崎県">宮崎県</option>
                      <option value="鹿児島県">鹿児島県</option>
                      <option value="沖縄県" disabled>
                        沖縄県
                      </option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="city-and-street" className="block text-[12px] text-dark-gray mb-2">
                      市区町村・番地 <span className="text-accent">*</span>
                    </label>
                    <input
                      type="text"
                      id="city-and-street"
                      name="city_and_street"
                      placeholder="渋谷区1-2-3"
                      required
                      value={address.city_and_street}
                      className={inputClass}
                      onChange={handleChangeAddress}
                      onBlur={validateCityAndStreet}
                      ref={cityAndStreetInput}
                    />
                    {streetError && <p className="text-[12px] text-accent mt-1">{streetError}</p>}
                  </div>
                  <div>
                    <label htmlFor="others" className="block text-[12px] text-dark-gray mb-2">
                      建物名・部屋番号
                    </label>
                    <input
                      type="text"
                      placeholder="○○マンション 101号室"
                      id="others"
                      name="others"
                      value={address.others}
                      className={inputClass}
                      onChange={handleChangeAddress}
                    />
                  </div>
                  <div>
                    <label htmlFor="phone-number" className="block text-[12px] text-dark-gray mb-2">
                      電話番号 <span className="text-accent">*</span>
                    </label>
                    <input
                      type="text"
                      id="phone-number"
                      name="phone_number"
                      placeholder="090-0000-0000"
                      required
                      value={address.phone_number}
                      className={inputClass}
                      onChange={handleChangeAddress}
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-3 cursor-pointer pt-1">
                      <input
                        type="checkbox"
                        name="with_sender_name"
                        className="w-4 h-4 rounded accent-black"
                        checked={address.with_sender_name}
                        onChange={(e) => setAddress((prev) => ({ ...prev, with_sender_name: e.target.checked }))}
                      />
                      <span className="text-[14px] text-main">注文者と受取人が異なる</span>
                    </label>
                  </div>

                  {address.with_sender_name && (
                    <div className="space-y-4 pt-4 border-t border-gray/40">
                      <div className="bg-bg rounded-xl p-4 space-y-2">
                        <p className="text-[12px] font-medium text-main">ご本人さま以外への配送について</p>
                        <ul className="space-y-1">
                          <li className="text-[12px] text-dark-gray leading-relaxed">・明細書など金額のわかるものは同封されません</li>
                          <li className="text-[12px] text-dark-gray leading-relaxed">・あらかじめ受取人の方に荷物が届くことをお伝えください</li>
                        </ul>
                      </div>
                      <div>
                        <label htmlFor="sender-name" className="block text-[12px] text-dark-gray mb-2">
                          送り主（ご注文者）のお名前 <span className="text-accent">*</span>
                        </label>
                        <input
                          type="text"
                          id="sender-name"
                          name="sender_name"
                          placeholder="田中 みどり"
                          className={inputClass}
                          required={address.with_sender_name}
                          value={address.sender_name}
                          onChange={handleChangeAddress}
                        />
                        <p className="text-[11px] text-dark-gray mt-2 leading-relaxed">
                          送り主のお名前があることで、相手が安心してお花を受け取ることができます
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </section>

        {isAddressExpanded && (
          <section className="bg-white rounded-2xl p-6 mb-6">
            <h2 className="text-[15px] font-medium text-black mb-6">お届けスケジュール</h2>
            <div className="space-y-7">
              <ScheduleSelector schedules={schedules} selectedInventories={inventories} onChange={(ids) => setInventoryIds(ids)} />
              <EstimatedDelivery schedules={schedules} selectedInventories={inventories} delayedWeek={delayedWeek} />
            </div>
          </section>
        )}

        {account ? (
          <section className="bg-white rounded-2xl p-6 mb-6">
            <h2 className="text-[15px] font-medium text-black mb-1">会員登録</h2>
            <p className="text-[12px] text-dark-gray mb-4">FLOWERアプリのアカウントで定期便もそのままご利用いただけます</p>
            <div className="flex items-center gap-3 bg-bg rounded-xl px-4 py-3">
              <div className="w-8 h-8 rounded-full bg-gray/40 flex items-center justify-center shrink-0">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 8L6.5 11.5L13 4.5" stroke="#828282" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-black truncate">{account.attributes.email}</p>
                <p className="text-[11px] text-dark-gray mt-0.5">ログイン中</p>
              </div>
              <div className="w-2 h-2 rounded-full bg-green-400 shrink-0" />
            </div>
          </section>
        ) : (
          <section className="bg-white rounded-2xl p-6 mb-6">
            <h2 className="text-[15px] font-medium text-black mb-1">会員登録</h2>
            <div className="flex items-start gap-1 rounded-lg px-3 py-2.5 mb-6" style={{ backgroundColor: 'rgba(228, 99, 39, 0.1)' }}>
              <svg className="w-4 h-4 mt-0.5 shrink-0" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="6.5" stroke="#e46327" strokeWidth="1.2" />
                <path d="M8 7.5V11" stroke="#e46327" strokeWidth="1.2" strokeLinecap="round" />
                <circle cx="8" cy="5.5" r="0.75" fill="#e46327" />
              </svg>
              <p className="text-[12px] text-accent">すでにFLOWERのアカウントをお持ちの方は、登録メールアドレスとパスワードをご入力ください</p>
            </div>
            <div className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-[12px] text-dark-gray mb-2">
                  メールアドレス <span className="text-accent">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  placeholder="flower@example.com"
                  required
                  value={email}
                  className={inputClass}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-[12px] text-dark-gray mb-2">
                  パスワード <span className="text-accent">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    placeholder="8文字以上"
                    minLength={8}
                    required
                    value={password}
                    className={inputClass}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-gray hover:text-black transition-colors"
                  >
                    {showPassword ? (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 8C1 8 3.5 3 8 3s7 5 7 5-2.5 5-7 5-7-5-7-5Z" stroke="currentColor" strokeWidth="1.2" />
                        <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2" />
                        <path d="M2 2l12 12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 8C1 8 3.5 3 8 3s7 5 7 5-2.5 5-7 5-7-5-7-5Z" stroke="currentColor" strokeWidth="1.2" />
                        <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="bg-white rounded-2xl p-6 mb-6">
          <h2 className="text-[15px] font-medium text-black mb-2">お支払い</h2>
          <Suspense fallback={<></>}>
            <CreditCardForm gap="m" creditCard={creditCardPromise} onInitialized={setCreditCard} onUpdate={handleUpdateCard} />
          </Suspense>
        </section>

        <section className="bg-white rounded-2xl p-6 mb-6">
          <h2 className="text-[15px] font-medium text-black mb-3">フラワーベースを追加</h2>
          <div className="inline-block bg-accent/10 rounded-md px-3 py-1.5 mb-4">
            <p className="text-[13px] text-accent">
              いま花器を一緒に買うと<span className="font-bold ml-1 border-b border-dotted border-accent">20%OFF</span>
            </p>
          </div>
          <VaseSelector inBulkProducts={inBulkOrderables} selected={inBulkOrderable} onChange={setInBulkOrderable} />
        </section>

        {order && (
          <section className="bg-white rounded-2xl p-6 mb-6">
            <h2 className="text-[15px] font-medium text-black mb-4">ご注文内容</h2>
            <PriceTable order={order} />
          </section>
        )}

        <div className="border-t border-gray/40 pt-5 mb-8 text-[13px] text-main leading-[1.8] space-y-2">
          <p className="font-medium text-black text-[14px]">今回お申し込みのプランについて</p>
          <p>季節の枝ものが届く定期便です。初回お届け完了後、2回目以降のスキップ・解約が可能になります（※次回発送の3日前正午までにお手続きください）。</p>
        </div>

        <EstimatedDelivery schedules={schedules} selectedInventories={inventories} delayedWeek={delayedWeek} className="bg-white mb-4" />

        <p className="text-center text-[13px] text-dark-gray mb-3">
          <a href="/terms" className="underline">
            利用規約
          </a>
          に同意して
        </p>
        <button
          type="submit"
          disabled={isSubmitting || isAlreadySubscribed}
          className="w-full py-5 bg-accent text-white text-[15px] font-medium rounded-full cursor-pointer hover:bg-main disabled:bg-main transition-colors"
        >
          {isAlreadySubscribed ? 'すでに定期便をご利用中です' : '申し込みを確定する'}
        </button>
        {errorMessage && <p className="text-[13px] text-accent mt-1">{errorMessage}</p>}

        <p className="mt-4 text-center text-[13px] text-dark-gray">
          <a href="/commercial" className="underline">
            注文キャンセルに関する注意事項
          </a>
        </p>
      </form>
    </article>
  )
}
